import gsap from 'gsap';

export default class CollectionSeoText extends HTMLElement {
  private toggleButton: HTMLButtonElement | null = null;
  private container: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private chunks: HTMLElement[] = [];

  private readMoreLabel: string = '';
  private readLessLabel: string = '';

  private isOpen = false;
  private contentHeight = 0;

  private openTl: gsap.core.Timeline | null = null;
  private closeTl: gsap.core.Timeline | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onButtonClickBound = this.onButtonClick.bind(this);

  static observedAttributes = ['data-is-open'];

  connectedCallback(): void {
    this.isOpen = this.dataset.isOpen === 'true';
    this.readMoreLabel = this.dataset.readMoreLabel || '';
    this.readLessLabel = this.dataset.readLessLabel || '';

    this.toggleButton = this.querySelector('[data-collection-seo-text-ref="toggle-button"]');
    this.container = this.querySelector('[data-collection-seo-text-ref="container"]');
    this.content = this.querySelector('[data-collection-seo-text-ref="content"]');
    this.chunks = Array.from(this.querySelectorAll('[data-collection-seo-text-ref="chunk"]'));

    this.addEventListeners();
    this.onResize();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);

    this.toggleButton?.addEventListener('click', this.onButtonClickBound);
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    this.toggleButton?.removeEventListener('click', this.onButtonClickBound);
  }

  onButtonClick(): void {
    this.dataset.isOpen = this.isOpen ? 'false' : 'true';
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'data-is-open' && oldValue !== newValue) {
      this.isOpen = newValue === 'true';

      if (this.isOpen) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  open(): void {
    this.isOpen = true;
    this.setExpanded(true);
    this.killTimelines();

    this.openTl = gsap.timeline();
    this.openTl.to(this.container, { height: this.contentHeight, duration: 0.9, ease: 'power3.inOut' }, 0);
    this.openTl.fromTo(
      this.chunks,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: 0.6,
        ease: 'power2.out',
      },
      0.3,
    );
  }

  close(): void {
    this.isOpen = false;
    this.setExpanded(false);
    this.killTimelines();

    this.closeTl = gsap.timeline();
    this.closeTl.to(this.container, { height: 0, duration: 0.6, ease: 'power3.out' }, 0);
    this.closeTl.to(
      this.chunks,
      {
        autoAlpha: 0,
        duration: 0.6,
        ease: 'power2.out',
      },
      0,
    );
  }

  setExpanded(expanded: boolean): void {
    const value = expanded ? 'true' : 'false';
    const label = expanded ? this.readLessLabel : this.readMoreLabel;

    if (this.toggleButton) {
      this.toggleButton.textContent = label;
      this.toggleButton.setAttribute('aria-expanded', value);
    }
  }

  onResize(): void {
    if (this.content) {
      this.contentHeight = this.content.getBoundingClientRect().height;
    }

    if (this.isOpen) {
      gsap.set(this.container, { height: this.contentHeight });
    }
  }

  killTimelines(): void {
    this.openTl?.kill();
    this.openTl = null;
    this.closeTl?.kill();
    this.closeTl = null;
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.killTimelines();
  }
}

customElements.define('collection-seo-text', CollectionSeoText);
